import {
  animate,
  state,
  style,
  transition,
  trigger
} from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, Optional, ViewChild, effect, input, signal } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, ReplaySubject, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AccountRepositoryService } from 'src/app/account/services/account-repository.service';
import { CustomShimmerComponent } from "src/app/components/custom-shimmer/custom-shimmer.component";
import { CustomValidators } from 'src/app/core/constants/validators';
import { Address, AddressType } from 'src/app/core/models/address.model';
import { MyOverlayRef } from 'src/app/core/services/overlay/myoverlay-ref';
import { CompleteAddressConcatPipe } from 'src/app/shared/pipes/complete-address-concat.pipe';
import { ExpectedDeliveryDates, ExpectedDeliveryService } from '../../services/expected-delivery.service';

export enum EstimatedDeliveryErrorMessage {
  SHIPROCKET_ERROR = 2157,
  PINCODE_NOT_FOUND_ERROR = 2156
}

@Component({
  standalone: true,
  selector: 'webstore-delivery-etd-status',
  templateUrl: './delivery-etd-status.component.html',
  styleUrls: ['./delivery-etd-status.component.scss'],
  animations: [
    trigger('slideDownEasy', [
      state('in', style({ transform: 'translateY(0)' })),
      transition('void => *', [
        style({ transform: 'translateY(-20%)' }),
        animate(300)
      ]),
      transition('* => void', [
        animate(100, style({ transform: 'translateY(0)' }))
      ])
    ])
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CustomShimmerComponent,
    CompleteAddressConcatPipe
  ]
})


export class DeliveryEtdStatusComponent implements OnInit, OnDestroy {

  readonly user = input<any>(null);
  readonly productUrlPath = input<string>('');
  readonly productIdInput = input<string>('');




  private _productId = signal<string>('');

  get productId() {
    return this._productId();
  }

  @ViewChild('offcanvasClose') offCanvasClose!: ElementRef;

  openAddressPanel: boolean = false;
  helperPanel: boolean = false;

  addresses$!: Observable<Address[]>;
  expectedDeliveryDates$!: Observable<ExpectedDeliveryDates>;
  destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  loading = false;
  noAddressesCreated = false;

  AddressType = AddressType;
  selectedAddressIndex: number | null = 0;
  errorMessage: string = '';

  deliveryPincode: any;
  private focusOutTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private accountRepositoryService: AccountRepositoryService,
    private formBuilder: UntypedFormBuilder,
    private expectedDeliveryService: ExpectedDeliveryService,
    private router: Router,
    @Optional() private myOverlayRef?: MyOverlayRef
  ) {
    this.deliveryPincode = this.formBuilder.control('', CustomValidators.pincode);

    effect(() => {
      const productIdValue = this.productIdInput();
      if (productIdValue) {
        this._productId.set(productIdValue);

        // Call getExpectedDelivery when productId is available
        if (!this.user()?.access_token) {
          // User is logged-out, call with empty pincode
          this.deliveryPincode.patchValue('');
          this.getExpectedDelivery('');
        }
      }
    });
  }

  async ngOnInit(): Promise<void> {
    /*User is logged in Address is fetched*/
    if (this.user()) {
      await this.fetchAddresses();
    }
  }


  getExpectedDelivery(pincode: string) {
    this.showLoader()
    this.expectedDeliveryDates$ = this.expectedDeliveryService.getExpectedDeliveryDates(this.productId, pincode).pipe(
      tap((estimatedDelivery: ExpectedDeliveryDates) => {
        this.errorMessage = '';
        if (estimatedDelivery?.code) {
          /* Errors were managed here*/
          switch (+estimatedDelivery.code) {
            case EstimatedDeliveryErrorMessage.SHIPROCKET_ERROR:
              this.errorMessage = 'Pincode is not serviceable';
              break;

            case EstimatedDeliveryErrorMessage.PINCODE_NOT_FOUND_ERROR:
              this.errorMessage = 'Not a valid pincode';
              break;

            default:
              break;
          }
        }
        this.hideLoader()
      }),
      catchError((error) => {
        this.hideLoader();
        return of(error);
      })
    );
  }

  async fetchAddresses() {
    await this.accountRepositoryService.getAddresses();

    // Create observable from signal for template compatibility
    this.addresses$ = new Observable<Address[]>((subscriber) => {
      const addresses = this.accountRepositoryService.addresses();
      if (addresses?.results) {
        const shippingAddresses = addresses.results.filter((address: any) => address.address_type === AddressType.SHIPPING);
        subscriber.next(shippingAddresses);

        // Handle address logic
        if (!shippingAddresses.length) {
          this.noAddressesCreated = true;
          /* No added address deliveryPincode patched with empty string */
          this.deliveryPincode.patchValue('');
          this.getExpectedDelivery('');
        } else {
          /* Avoiding patching of first address if user already entered address  */
          if (!this.deliveryPincode.value) {
            this.deliveryPincode.patchValue(shippingAddresses[0]?.pincode)
          }
          this.getExpectedDelivery(this.deliveryPincode.value);
        }
      } else {
        subscriber.next([]);
      }
      subscriber.complete();
    });
  }

  private showLoader() {
    this.loading = true;
  }

  private hideLoader() {
    this.loading = false;
  }

  /* Toggling SavedAddressPanel on change button click */
  toggleSavedAddressPanel() {
    this.openAddressPanel = !this.openAddressPanel;
    this.helperPanel = false;
  }

  /* Opening SavedAddressPanel on input element click */
  openSavedAddressPanel() {
    this.openAddressPanel = true;
    this.helperPanel = false;
  }

  /* Closing SavedAddressPanel on outside click on api call */
  closeSavedAddressPanel() {
    this.openAddressPanel = false;
  }

  /* Toggling Estimated delivery detail on information-icon click  */
  aboutDeliveryPanel() {
    this.helperPanel = !this.helperPanel;
    this.closeSavedAddressPanel();
  }

  /* Closing Estimated delivery detail on outside click  click  */
  helperPanelToggle() {
    this.helperPanel = false;
  }

  /* On saved address click patching address from saved address in desktop */
  patchPincode(pincode: string) {
    this.deliveryPincode.patchValue(pincode);
    this.getExpectedDelivery(pincode);
    this.closeSavedAddressPanel();
  }

  /* On saved address click patching address from saved address in mobile */
  patchPincodeForMobile(pincode: string, index: number) {
    this.deliveryPincode.patchValue(pincode);
    this.selectedAddressIndex = index;
    this.getExpectedDelivery(pincode);
    this.closeOffcanvas();
  }

  /* Resetting selectedAddressIndex on pincode change in mobile */
  unPatchAddress() {
    this.selectedAddressIndex = null;
  }

  /* Navigating to login page with returnUrl queryParams to navigate back after successful login */
  navigateToLogin() {
    this.closeOffcanvas();
    this.myOverlayRef?.close();
    this.router.navigate(['auth/login'],
      {
        queryParams: {
          returnUrl: `products/${this.productUrlPath()}`
        }
      });
  }

  /* Navigating to add address page with returnUrl queryParams to navigate back after successful address addition */
  navigateToCreateAdress() {
    this.closeOffcanvas();

    this.myOverlayRef?.close();

    this.router.navigate(['account/address-book'],
      {
        queryParams: {
          returnUrl: `products/${this.productUrlPath()}`
        }
      });
  }

  /* Closing closeOffcanvas in mobile on api call */
  closeOffcanvas() {
    if (this.offCanvasClose.nativeElement) {
      this.offCanvasClose.nativeElement.click();
    }
  }

  expectedDeliveryForPincode() {
    /* Checking is deliveryPincode control is valid and having value */
    if (this.deliveryPincode.valid && this.deliveryPincode.value) {
      this.getExpectedDelivery(this.deliveryPincode.value);
      this.closeSavedAddressPanel();
    } else {
      /* DeliveryPincode control is invalid closing SavedAddressPanel */
      this.closeSavedAddressPanel();
    }
  }

  focusOutEvent() {
    this.focusOutTimeoutId = setTimeout(() => this.openAddressPanel = false, 300);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    // Clear timeout to prevent memory leaks
    if (this.focusOutTimeoutId) {
      clearTimeout(this.focusOutTimeoutId);
      this.focusOutTimeoutId = null;
    }
  }
}
